import React, { useMemo, useState, useEffect } from 'react';
import "./styles/topicsQuestions.css";
import { CSSTransition, TransitionGroup } from 'react-transition-group';

const TopicsQuestionsStep = ({ formData, updateFormData }) => {
  const maxQuestions = 7;
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [previousVisibleQuestions, setPreviousVisibleQuestions] = useState([]);

  // Ensure questions array is properly initialized 
  useEffect(() => {
    if (!formData.questions || !Array.isArray(formData.questions)) {
      const initialQuestions = Array(maxQuestions).fill().map(() => ({ topic: '', text: '' }));
      updateFormData('questions', initialQuestions);
    }
  }, []);

  // Simplified visibility logic that properly handles all cases
  useEffect(() => {
    setPreviousVisibleQuestions(visibleQuestions);
    
    const newVisibleQuestions = [];
    const questions = formData.questions || [];
    
    // First 4 questions are always visible (0-3)
    for (let i = 0; i < 4; i++) {
      newVisibleQuestions.push(i);
    }
    
    // For questions 5-7 (indices 4-6)
    for (let i = 4; i < maxQuestions; i++) {
      // Show this question if ANY of these conditions are true:
      // 1. This question has data
      // 2. The previous question has data
      // 3. Any later question has data
      
      if (hasData(questions[i])) {
        newVisibleQuestions.push(i);
        continue;
      }
      
      if (i > 0 && hasData(questions[i-1])) {
        newVisibleQuestions.push(i);
        continue;
      }
      
      // Check if any later questions have content
      let showDueToLaterContent = false;
      for (let j = i + 1; j < maxQuestions; j++) {
        if (hasData(questions[j])) {
          showDueToLaterContent = true;
          break;
        }
      }
      
      if (showDueToLaterContent) {
        newVisibleQuestions.push(i);
      }
    }
    
    setVisibleQuestions(newVisibleQuestions);
  }, [formData.questions]);

  const filledCount = useMemo(() => {
    return (formData.questions || []).reduce((count, q) => {
      if (q.topic || q.text) count++;
      return count;
    }, 0);
  }, [formData.questions]);

  const handleTopicUpdate = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    updateFormData('questions', newQuestions);
  };

  const handleInputChange = (index, field, value) => {
    handleTopicUpdate(index, field, value);
  };

  const hasData = (q) => !!(q?.topic || q?.text);

  // Simplified and clearer disabled logic
  const isQuestionDisabled = (questions, index) => {
    if (index < 3) {
      return false; // First 3 questions are always enabled
    }

    if (index === 3) {
      return !hasData(questions[2]); // Question 4 depends on question 3
    }

    // For questions 5-7:
    if (hasData(questions[index])) {
      return false;
    }

    return !hasData(questions[index - 1]);
  };

  const getTransitionItems = () => {
    const questions = formData.questions || [];
    const items = [];

    for (let i = 0; i < maxQuestions; i++) {
      if (visibleQuestions.includes(i)) {
        items.push({
          id: `question-${i}`,
          index: i,
          question: questions[i] || { topic: '', text: '' },
          disabled: isQuestionDisabled(questions, i),
        });
      }
    }

    return items;
  };

  return (
    <div className="step-content">
      <h2>Indicate topics and questions</h2>
      <TransitionGroup className="topics-container">
        {getTransitionItems().map((item) => (
          <CSSTransition
            key={item.id}
            timeout={300}
            classNames={{
              enter: 'topic-item-enter',
              enterActive: 'topic-item-enter-active',
              exit: 'topic-item-exit',
              exitActive: 'topic-item-exit-active',
            }}
            appear
          >
            <div
              className={`topic-bubble topic-item ${item.disabled ? 'disabled' : ''}`}
            >
              <input
                type="text"
                value={item.question.topic || ''}
                onChange={(e) => handleInputChange(item.index, 'topic', e.target.value)}
                placeholder={`Topic ${item.index + 1}`}
                disabled={item.disabled}
              />
              <input
                type="text"
                value={item.question.text || ''}
                onChange={(e) => handleInputChange(item.index, 'text', e.target.value)}
                placeholder="Question"
                disabled={item.disabled}
              />
            </div>
          </CSSTransition>
        ))}
      </TransitionGroup>
      <p className="topics-hint">
        {filledCount} of {maxQuestions} questions added
      </p>
    </div>
  );
};

export default TopicsQuestionsStep;